import { createContext, useContext, useState, useEffect, type ReactNode } from "react";

interface DevFeatures {
  endpointsPageAvailable: boolean;
  EndpointsPage: React.ComponentType | null;
}

const DevFeaturesContext = createContext<DevFeatures>({
  endpointsPageAvailable: false,
  EndpointsPage: null,
});

export function useDevFeatures(): DevFeatures {
  return useContext(DevFeaturesContext);
}

// Use Vite's glob import - returns empty object if file doesn't exist (production)
// This avoids build-time resolution errors for missing files
const devPageModules = import.meta.glob<{ default: React.ComponentType }>(
  "../pages/EndpointsPage.tsx"
);

export function DevFeaturesProvider({ children }: { children: ReactNode }): JSX.Element {
  const [endpointsPageAvailable, setEndpointsPageAvailable] = useState(false);
  const [EndpointsPage, setEndpointsPage] = useState<React.ComponentType | null>(null);

  useEffect(() => {
    // Check if the EndpointsPage module exists in the glob result
    const modulePath = "../pages/EndpointsPage.tsx";
    const loader = devPageModules[modulePath];
    
    if (loader) {
      // File exists - load it
      loader()
        .then((module) => {
          setEndpointsPage(() => module.default);
          setEndpointsPageAvailable(true);
        })
        .catch(() => {
          setEndpointsPageAvailable(false);
          setEndpointsPage(null);
        });
    } else {
      // File doesn't exist (production build)
      setEndpointsPageAvailable(false);
      setEndpointsPage(null);
    }
  }, []);

  return (
    <DevFeaturesContext.Provider value={{ endpointsPageAvailable, EndpointsPage }}>
      {children}
    </DevFeaturesContext.Provider>
  );
}
